/**
 * NewProposalModal — the Contracts view's "New proposal" / "New contract"
 * create flow (the `docKind` prop selects which; defaults to 'proposal').
 *
 * On-brand GlassPanel dialog with three field groups: title, account
 * (select over the live accounts store), and 1+ line items (description ×
 * qty × unit price) with a running total. Validation and totals live in
 * the PURE `buildNewProposal` helper (lib/crm/newProposal); persistence
 * reuses the EXISTING documents store (`createDocument`), so the new
 * proposal lands in the same collection the Contracts list subscribes to
 * and appears there immediately — openable/previewable via the existing
 * proposal doc renderer.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileSignature, FileText, Minus, Plus, UserPlus, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import {
  buildNewProposal,
  type NewDocKind,
  type NewProposalLineInput,
} from '../lib/crm/newProposal';
import { buildInlineCustomer } from '../lib/crm/inlineCustomer';
import {
  DEFAULT_PROPOSAL_TEMPLATE_ID,
  PROPOSAL_TEMPLATES,
} from '../lib/crm/proposalTemplates';
import { createDocument } from '../lib/db/documents';
import { createAccount } from '../lib/db/accounts';
import type { Account, BusinessDoc } from '../lib/db/types';

interface Props {
  /** Accounts to pick from (the caller subscribes to the accounts store). */
  accounts: Account[];
  /** Signed-in operator, stamped as ownerName/createdBy. */
  ownerName?: string;
  createdBy?: string;
  onClose: () => void;
  /** Fires with the persisted document after a successful create. */
  onCreated: (doc: BusinessDoc) => void;
  /**
   * Which document kind this dialog creates. Defaults to 'proposal'
   * (the original flow); the Contracts view's "New contract" action
   * passes 'contract' to reuse the SAME form + create path while
   * `createDocument` fills the contract template.
   */
  docKind?: NewDocKind;
}

const EMPTY_LINE: NewProposalLineInput = { description: '', qty: '1', unitPrice: '' };

/** Sentinel <option> value for the inline "New customer" picker entry. */
const NEW_CUSTOMER_VALUE = '__new-customer__';

function usd(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

export function NewProposalModal({
  accounts,
  ownerName,
  createdBy,
  onClose,
  onCreated,
  docKind = 'proposal',
}: Props) {
  const kindNoun = docKind === 'contract' ? 'contract' : 'proposal';
  const [title, setTitle] = useState('');
  const [accountId, setAccountId] = useState('');
  const [lines, setLines] = useState<NewProposalLineInput[]>([{ ...EMPTY_LINE }]);
  // Branded visual template (proposals only) — drives the Preview / Print
  // design. See lib/crm/proposalTemplates for the actual renders.
  const [templateId, setTemplateId] = useState<string>(
    DEFAULT_PROPOSAL_TEMPLATE_ID,
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Inline "New customer" state — the account picker offers a sentinel
  // option that opens a mini create form (name + optional email) which
  // writes through the EXISTING accounts store and selects the result.
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [custName, setCustName] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custErrors, setCustErrors] = useState<string[]>([]);
  const [custBusy, setCustBusy] = useState(false);
  // Accounts created inline, merged into the picker so the new record is
  // selectable immediately even if the caller's subscription hasn't
  // re-emitted yet.
  const [inlineAccounts, setInlineAccounts] = useState<Account[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const allAccounts = useMemo(() => {
    const known = new Set(accounts.map((a) => a.id));
    return [...accounts, ...inlineAccounts.filter((a) => !known.has(a.id))];
  }, [accounts, inlineAccounts]);

  const account = useMemo(
    () => allAccounts.find((a) => a.id === accountId) ?? null,
    [allAccounts, accountId],
  );

  const handleCreateCustomer = async () => {
    const built = buildInlineCustomer({
      name: custName,
      email: custEmail,
      ownerName,
      existingNames: allAccounts.map((a) => a.name),
    });
    if (!built.ok) {
      setCustErrors(built.errors);
      return;
    }
    setCustErrors([]);
    setCustBusy(true);
    try {
      const created = await createAccount(built.account);
      setInlineAccounts((prev) => [...prev, created]);
      setAccountId(created.id);
      setNewCustomerOpen(false);
      setCustName('');
      setCustEmail('');
    } catch (err) {
      setCustErrors([
        err instanceof Error ? err.message : 'Could not create the customer.',
      ]);
    } finally {
      setCustBusy(false);
    }
  };

  // Live running total — reuses the same pure math the save path uses, so
  // what the operator sees is exactly what gets persisted.
  const runningTotal = useMemo(() => {
    const built = buildNewProposal({
      title: title || 'draft',
      accountId: accountId || 'draft',
      accountName: account?.name ?? 'draft',
      lineItems: lines,
    });
    return built.ok ? built.totals.total : null;
  }, [title, accountId, account, lines]);

  const setLine = (i: number, patch: Partial<NewProposalLineInput>) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  const handleCreate = async () => {
    const built = buildNewProposal({
      title,
      accountId,
      accountName: account?.name ?? '',
      lineItems: lines,
      ownerName,
      createdBy,
      kind: docKind,
      templateId: docKind === 'proposal' ? templateId : undefined,
    });
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    setBusy(true);
    try {
      const doc = await createDocument(built.doc);
      onCreated(doc);
    } catch (err) {
      setErrors([err instanceof Error ? err.message : `Could not create the ${kindNoun}.`]);
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-proposal-title"
        tabIndex={-1}
        style={{ width: 'min(620px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            {docKind === 'contract' ? (
              <FileSignature size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            ) : (
              <FileText size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            )}
            <div style={{ flex: 1 }}>
              <span className="t-eyebrow">Contracts</span>
              <h2
                id="new-proposal-title"
                className="t-section-title"
                style={{ margin: '4px 0 0' }}
              >
                {docKind === 'contract' ? 'New contract' : 'New proposal'}
              </h2>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={busy}
            >
              <X size={13} />
            </button>
          </header>

          <div style={{ display: 'grid', gap: 16, padding: '12px 0 0' }}>
            <div>
              <label className="t-eyebrow" htmlFor="np-title" style={{ display: 'block', marginBottom: 4 }}>
                Title
              </label>
              <input
                id="np-title"
                className="glass-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. AI Operations rollout — Acme Corp"
                disabled={busy}
                maxLength={140}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label className="t-eyebrow" htmlFor="np-account" style={{ display: 'block', marginBottom: 4 }}>
                Account
              </label>
              <select
                id="np-account"
                className="glass-input"
                value={newCustomerOpen ? NEW_CUSTOMER_VALUE : accountId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === NEW_CUSTOMER_VALUE) {
                    setAccountId('');
                    setNewCustomerOpen(true);
                  } else {
                    setNewCustomerOpen(false);
                    setCustErrors([]);
                    setAccountId(v);
                  }
                }}
                disabled={busy || custBusy}
                style={{ width: '100%' }}
              >
                <option value="">Select an account…</option>
                {allAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
                <option value={NEW_CUSTOMER_VALUE}>＋ New customer…</option>
              </select>
              {allAccounts.length === 0 && !newCustomerOpen && (
                <p className="t-meta" style={{ margin: '6px 0 0' }}>
                  No accounts yet — pick “＋ New customer…” to create one right
                  here.
                </p>
              )}

              {newCustomerOpen && (
                <div
                  style={{
                    display: 'grid',
                    gap: 8,
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <span className="t-eyebrow">New customer</span>
                  <input
                    className="glass-input"
                    aria-label="New customer name"
                    placeholder="Customer / company name"
                    value={custName}
                    onChange={(e) => setCustName(e.target.value)}
                    disabled={custBusy || busy}
                    maxLength={120}
                  />
                  <input
                    className="glass-input"
                    aria-label="New customer email"
                    placeholder="Email (optional)"
                    type="email"
                    value={custEmail}
                    onChange={(e) => setCustEmail(e.target.value)}
                    disabled={custBusy || busy}
                    maxLength={120}
                  />
                  {custErrors.length > 0 && (
                    <div className="login-banner" role="alert">
                      {custErrors.map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={() => {
                        setNewCustomerOpen(false);
                        setCustErrors([]);
                      }}
                      disabled={custBusy || busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="glass-btn glass-btn--primary"
                      onClick={() => void handleCreateCustomer()}
                      disabled={custBusy || busy}
                    >
                      <UserPlus size={13} />{' '}
                      {custBusy ? 'Creating…' : 'Create customer'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {docKind === 'proposal' && (
              <div>
                <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                  Template
                </div>
                <div
                  role="radiogroup"
                  aria-label="Proposal template"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 8,
                  }}
                >
                  {PROPOSAL_TEMPLATES.map((t) => {
                    const selected = templateId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setTemplateId(t.id)}
                        disabled={busy}
                        style={{
                          textAlign: 'left',
                          cursor: 'pointer',
                          padding: '10px 12px',
                          borderRadius: 12,
                          background: selected
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(255,255,255,0.03)',
                          border: selected
                            ? `1px solid ${t.accent}`
                            : '1px solid rgba(255,255,255,0.08)',
                          boxShadow: selected
                            ? `0 0 0 1px ${t.accent}55, 0 6px 18px rgba(0,0,0,0.25)`
                            : 'none',
                          transition: 'border-color .15s, background .15s',
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            display: 'block',
                            height: 6,
                            borderRadius: 3,
                            marginBottom: 8,
                            background: `linear-gradient(90deg, ${t.accent}, ${t.accent2})`,
                          }}
                        />
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#fff',
                          }}
                        >
                          {t.name}
                        </span>
                        <span
                          className="t-meta"
                          style={{ display: 'block', marginTop: 3, lineHeight: 1.45 }}
                        >
                          {t.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="t-meta" style={{ margin: '6px 0 0' }}>
                  Sets the branded cover, colors, and layout used by Preview /
                  Print.
                </p>
              </div>
            )}

            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>
                Line items
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {lines.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 72px 110px 34px',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <input
                      className="glass-input"
                      aria-label={`Line ${i + 1} description`}
                      placeholder="Description"
                      value={line.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                      disabled={busy}
                    />
                    <input
                      className="glass-input"
                      aria-label={`Line ${i + 1} quantity`}
                      placeholder="Qty"
                      type="number"
                      min={1}
                      value={String(line.qty)}
                      onChange={(e) => setLine(i, { qty: e.target.value })}
                      disabled={busy}
                    />
                    <input
                      className="glass-input"
                      aria-label={`Line ${i + 1} unit price`}
                      placeholder="Unit price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={String(line.unitPrice)}
                      onChange={(e) => setLine(i, { unitPrice: e.target.value })}
                      disabled={busy}
                    />
                    <button
                      type="button"
                      className="glass-btn"
                      aria-label={`Remove line ${i + 1}`}
                      onClick={() =>
                        setLines((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      disabled={busy || lines.length <= 1}
                      style={{ padding: '6px 8px' }}
                    >
                      <Minus size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                }}
              >
                <button
                  type="button"
                  className="glass-btn"
                  onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
                  disabled={busy}
                >
                  <Plus size={13} /> Add line item
                </button>
                <span className="t-meta">
                  Total:{' '}
                  <strong style={{ color: '#fff' }}>
                    {runningTotal == null ? '—' : usd(runningTotal)}
                  </strong>
                </span>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="login-banner" role="alert">
                {errors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </div>
            )}
          </div>

          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 14,
              marginTop: 4,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button type="button" className="glass-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={() => void handleCreate()}
              disabled={busy}
            >
              <Plus size={13} /> {busy ? 'Creating…' : `Create ${kindNoun}`}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>,
    document.body,
  );
}
